// src/admin/pages/shipping/ShippingManagement.tsx
import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Drawer,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Chip,
    Tooltip,
    InputAdornment,
    Pagination,
    Divider,
    Backdrop,
    LinearProgress,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    LocalShipping,
    Save,
    Cancel,
    Search as SearchIcon,
    Clear as ClearIcon,
    Close as CloseIcon,
    LocalOffer as FreeShippingIcon,
} from '@mui/icons-material';
import { ShippingCountry, ShippingState } from './shipping';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { formatNumber } from '../../../util/Formatcurrency';
import {
    useShippingPaginated,
    useShippingStats,
    useShippingMutations,
} from '../../../hooks/useShipping';
import ToastNotification from '../common/Toastnotification';

const ITEMS_PER_PAGE = 10;

interface ToastState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
    type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

    const lightPalette: Record<string, PaletteEntry> = {
        warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
        info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
        primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
        success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
        error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
        secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
        default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
    };

    const darkPalette: Record<string, PaletteEntry> = {
        warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
        info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
        primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
        success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
        error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
        secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
        default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
    };

    const palette = isDark ? darkPalette : lightPalette;
    const p = palette[color] ?? palette.default;

    return {
        backgroundColor: p.bg,
        borderColor: p.border,
        color: p.text,
        fontWeight: 600,
        border: `1px solid ${p.border}`,
        '& .MuiChip-icon': { color: p.iconColor },
        '& .MuiChip-label': { color: p.text },
    };
};

interface LoadingOverlayProps {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
    const [rect, setRect] = useState<DOMRect | null>(null);
    const theme = useTheme();

    const overlayBg = theme.palette.mode === 'dark'
        ? 'rgba(26, 29, 36, 0.85)'
        : 'rgba(255, 255, 255, 0.85)';

    React.useEffect(() => {
        const measure = () => {
            if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
        };
        measure();
        const el = wrapperRef.current;
        window.addEventListener('resize', measure);
        el?.addEventListener('scroll', measure);
        return () => {
            window.removeEventListener('resize', measure);
            el?.removeEventListener('scroll', measure);
        };
    }, [wrapperRef]);

    if (!rect) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: rect.top, left: rect.left,
                width: rect.width, height: rect.height,
                backgroundColor: overlayBg,
                backdropFilter: 'blur(2px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
            }}
        >
            <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading shipping rates...
                </Typography>
            </Box>
        </Box>
    );
};

const ShippingManagement: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    const { currentUser, hasPermission, isSuperAdmin } = useAuth();
    const { companyProfile } = useCompany();

    const companyCurrency = companyProfile?.currency || 'LKR';
    const currencySymbol = getCurrencySymbol(companyCurrency);
    const formatNum = (n: number, decimals = 2) => formatNumber(n, decimals);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
    const [editCountry, setEditCountry] = useState<ShippingCountry | null>(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
    const [deleteTarget, setDeleteTarget] = useState<ShippingCountry | null>(null);
    const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

    // ── Full-screen overlays (mirror EnhancedProductDrawer pattern) ───────────
    const [deletingOverlay, setDeletingOverlay] = useState(false);
    const [savingOverlay, setSavingOverlay] = useState(false);

    // ── Pagination & filter state ─────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const canView = isSuperAdmin || hasPermission('shipping.view');
    const canCreate = isSuperAdmin || hasPermission('shipping.create');
    const canEdit = isSuperAdmin || hasPermission('shipping.edit');
    const canDelete = isSuperAdmin || hasPermission('shipping.delete');

    // ── Form state ────────────────────────────────────────────────────────────
    const [form, setForm] = useState<ShippingCountry>({
        id: '', name: '', code: '', defaultShippingCost: 0, freeShippingLimit: 0, states: [],
    });

    const statesContainerRef = useRef<HTMLDivElement>(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);

    // ── React Query hooks ─────────────────────────────────────────────────────
    const {
        data: shippingData,
        isLoading: shippingLoading,
        isFetching: shippingIsFetching,
        isError: shippingIsError,
        error: shippingErrorObj,
    } = useShippingPaginated({
        page: currentPage,
        search: searchQuery,
        pageSize: ITEMS_PER_PAGE,
    });

    const { data: statsData } = useShippingStats();
    const { create, update, remove } = useShippingMutations(currentUser?.uid || '');

    // ── Derived values ────────────────────────────────────────────────────────
    const countries = shippingData?.countries ?? [];
    const totalItems = shippingData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const totalCountries = statsData?.totalCountries ?? 0;
    const totalStates = statsData?.totalStates ?? 0;
    const countriesWithFreeShipping = statsData?.countriesWithFreeShipping ?? 0;
    const loading = shippingLoading || shippingIsFetching;

    // ── Auto-scroll to bottom of states list when a new state is added ────────
    React.useEffect(() => {
        if (statesContainerRef.current && form.states.length > 0) {
            statesContainerRef.current.scrollTo({
                top: statesContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [form.states.length]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setToast({ open: true, message, severity });
    };
    const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

    const hasActiveSearch = searchQuery !== '';
    const countriesWithStates = countries.filter(c => c.states.length > 0).length;

    // ── Reset to page 1 ───────────────────────────────────────────────────────
    const resetToFirstPage = () => {
        setCurrentPage(1);
    };

    // ── Pagination handler ────────────────────────────────────────────────────
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // ── Search ────────────────────────────────────────────────────────────────
    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setSearchQuery(searchInput);
            resetToFirstPage();
        }
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchQuery('');
        resetToFirstPage();
        showToast('Search cleared', 'info');
    };

    // ── Form helpers ──────────────────────────────────────────────────────────
    const resetForm = () => {
        setForm({ id: '', name: '', code: '', defaultShippingCost: 0, freeShippingLimit: 0, states: [] });
        setEditCountry(null);
    };

    const handleAdd = () => {
        resetForm();
        setDrawerMode('add');
        setDrawerOpen(true);
    };

    const handleEdit = (country: ShippingCountry) => {
        setForm({ ...country, freeShippingLimit: country.freeShippingLimit ?? 0 });
        setEditCountry(country);
        setDrawerMode('edit');
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        resetForm();
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = (id: string) => {
        const country = countries.find(c => c.id === id) ?? null;
        setDeleteTarget(country);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            setDeleteDialogLoading(true);
            // Close the confirm dialog immediately, then show the full-screen
            // overlay while the async work completes — mirrors EnhancedProductDrawer.
            setDeleteTarget(null);
            setDeletingOverlay(true);

            await remove.mutateAsync(deleteTarget.id);

            resetToFirstPage();
            showToast('Shipping country deleted successfully', 'success');
        } catch (err: any) {
            showToast(`Failed to delete: ${err.message || 'Unknown error'}`, 'error');
        } finally {
            setDeleteDialogLoading(false);
            setDeletingOverlay(false);
        }
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <LocalShipping sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
                        <Typography variant="h5" gutterBottom>Access Denied</Typography>
                        <Typography variant="body1" color="text.secondary">
                            You don't have permission to view shipping rates. Please contact your administrator.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const handleDeleteCancel = () => setDeleteTarget(null);

    // ── Save (create or update) ───────────────────────────────────────────────
    const handleSave = async () => {
        if (!form.name || !form.code) {
            showToast('Country name & code are required', 'error');
            return;
        }
        if (form.code.length > 3) {
            showToast('Country code must be 3 characters or less', 'error');
            return;
        }
        if (form.defaultShippingCost < 0) {
            showToast('Default shipping cost cannot be negative', 'error');
            return;
        }
        if (form.freeShippingLimit < 0) {
            showToast('Free shipping limit cannot be negative', 'error');
            return;
        }
        for (let i = 0; i < form.states.length; i++) {
            const state = form.states[i];
            if (!state.name.trim()) {
                showToast(`State #${i + 1} name is required`, 'error');
                return;
            }
            if (state.shippingCost < 0) {
                showToast(`State #${i + 1} shipping cost cannot be negative`, 'error');
                return;
            }
            if ((state.freeShippingLimit ?? 0) < 0) {
                showToast(`State #${i + 1} free shipping limit cannot be negative`, 'error');
                return;
            }
        }

        try {
            setSaveLoading(true);
            // Close the drawer immediately, then show the full-screen overlay
            // while the async work completes — mirrors EnhancedProductDrawer.
            setDrawerOpen(false);
            setSavingOverlay(true);

            if (drawerMode === 'edit' && editCountry) {
                await update.mutateAsync({ id: editCountry.id, country: form });
                showToast('Shipping country updated successfully', 'success');
            } else {
                await create.mutateAsync(form);
                showToast('Shipping country created successfully', 'success');
            }
            resetForm();
            resetToFirstPage();
        } catch (err: any) {
            // Re-open the drawer so the user can correct the error
            setDrawerOpen(true);
            showToast(`Failed to save: ${err.message || 'Unknown error'}`, 'error');
        } finally {
            setSaveLoading(false);
            setSavingOverlay(false);
        }
    };

    // ── State management inside form ──────────────────────────────────────────
    const addState = () => {
        setForm(prev => ({
            ...prev,
            states: [...prev.states, { id: Date.now().toString(), name: '', shippingCost: 0, freeShippingLimit: 0 }],
        }));
    };

    const updateState = (index: number, field: keyof ShippingState, value: any) => {
        const states = [...form.states];
        states[index] = { ...states[index], [field]: value };
        setForm({ ...form, states });
    };

    const removeState = (index: number) => {
        setForm(prev => ({ ...prev, states: prev.states.filter((_, i) => i !== index) }));
    };

    // ── Refresh ───────────────────────────────────────────────────────────────
    const handleRefresh = () => {
        resetToFirstPage();
        showToast('Shipping rates refreshed', 'info');
    };

    // ── Initial full-page loading state ───────────────────────────────────────
    if (loading && countries.length === 0) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
                <CircularProgress size={40} />
                <Typography variant="body1" sx={{ ml: 2 }}>Loading shipping rates...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: { md: '100%' },
                display: 'flex',
                flexDirection: 'column',
                overflow: { xs: 'visible', md: 'hidden' },
                p: { xs: 2, sm: 3 },
            }}
        >
            {/* ── Fixed Header ── */}
            <Box sx={{ flexShrink: 0 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <LocalShipping sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                                Shipping Management
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage delivery costs by country &amp; state
                            </Typography>
                        </Box>
                    </Stack>
                    {canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAdd}
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                        >
                            Add Country
                        </Button>
                    )}
                </Stack>

                {/* {shippingIsError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {(shippingErrorObj as Error)?.message || 'Failed to fetch shipping rates'}
                    </Alert>
                )} */}

                {/* ── Stats Cards ── */}
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Countries</Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {totalCountries}
                                </Typography>
                                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                                        <Typography variant="caption" color="success.main" noWrap>
                                            {countriesWithStates} with states
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {totalCountries - countriesWithStates} without states
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total States</Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {totalStates}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {totalCountries > 0
                                            ? `${(totalStates / totalCountries).toFixed(1)} avg per country`
                                            : '—'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Free Shipping</Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {countriesWithFreeShipping}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {totalCountries > 0
                                            ? `${((countriesWithFreeShipping / totalCountries) * 100).toFixed(1)}% have free limit`
                                            : '—'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                                    {hasActiveSearch ? 'Matching Results' : 'This Page'}
                                </Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {countries.length}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {hasActiveSearch
                                            ? `of ${totalItems} total`
                                            : `page ${currentPage} of ${Math.max(1, totalPages)}`}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Search ── */}
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <TextField
                            placeholder="Search countries, codes, or states... (Press Enter)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            sx={{ flexGrow: 1, maxWidth: { sm: 500 } }}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                ),
                                endAdornment: hasActiveSearch && (
                                    <InputAdornment position="end">
                                        <Tooltip title="Clear search">
                                            <IconButton size="small" onClick={clearSearch}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {hasActiveSearch
                                ? `Found ${countries.length} matching results`
                                : `Showing ${countries.length} of ${totalItems} countries`}
                        </Typography>
                    </Stack>
                </Paper>
            </Box>

            {/* ── Scrollable Body ── */}
            <Box
                sx={{
                    flex: 1,
                    overflow: { xs: 'visible', md: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 'auto', md: 0 },
                }}
            >
                {countries.length === 0 && !loading && (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Card sx={{ width: '100%', maxWidth: 500 }}>
                            <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                <LocalShipping sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                                <Typography variant="h5" color="text.secondary" gutterBottom>
                                    {hasActiveSearch ? 'No Countries Match Your Search' : 'No Shipping Rates Found'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                                    {hasActiveSearch
                                        ? 'Try adjusting your search criteria or add a new country.'
                                        : 'Get started by adding your first shipping country.'}
                                </Typography>
                                {hasActiveSearch ? (
                                    <Button onClick={clearSearch} variant="outlined" startIcon={<ClearIcon />}>
                                        Clear Search
                                    </Button>
                                ) : canCreate && (
                                    <Button onClick={handleAdd} variant="contained" startIcon={<Add />}>
                                        Add Country
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {countries.length > 0 && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
                        <TableContainer
                            component={Paper}
                            ref={tableWrapperRef}
                            sx={{
                                flex: 1,
                                overflow: { xs: 'auto', md: 'auto' },
                                maxHeight: { md: '100%' },
                                mb: 2,
                                position: 'relative',
                                '& .MuiTable-root': { minWidth: { xs: 600, md: 900 } },
                            }}
                        >
                            <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Country</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Default Cost</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Free Shipping Limit</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>States</TableCell>
                                        {(canEdit || canDelete) && (
                                            <TableCell align="right">Actions</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {countries.map(country => (
                                        <TableRow
                                            key={country.id}
                                            hover
                                            sx={{
                                                '&:hover': { backgroundColor: 'action.hover' },
                                                '&:last-child td, &:last-child th': { borderBottom: 0 },
                                            }}
                                        >
                                            <TableCell>
                                                <Typography fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                                    {country.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary"
                                                    sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                    {country.states.length} state{country.states.length !== 1 ? 's' : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={country.code} size="small" variant="outlined"
                                                    sx={getSoftChipSx('primary', isDark)} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {currencySymbol} {formatNum(country.defaultShippingCost)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                                {(country.freeShippingLimit ?? 0) > 0 ? (
                                                    <Chip
                                                        icon={<FreeShippingIcon />}
                                                        label={`${currencySymbol} ${formatNum(country.freeShippingLimit)}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={getSoftChipSx('success', isDark)}
                                                    />
                                                ) : (
                                                    <Chip label="No limit" size="small" variant="outlined"
                                                        sx={getSoftChipSx('default', isDark)} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                                <Chip
                                                    label={`${country.states.length} state${country.states.length !== 1 ? 's' : ''}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={getSoftChipSx(country.states.length > 0 ? 'success' : 'default', isDark)}
                                                />
                                            </TableCell>
                                            {(canEdit || canDelete) && (
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                                                        {canEdit && (
                                                            <Tooltip title="Edit Country">
                                                                <IconButton onClick={() => handleEdit(country)} size="small" color="warning">
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {canDelete && (
                                                            <Tooltip title="Delete Country">
                                                                <IconButton color="error" onClick={() => handleDelete(country.id)} size="small">
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

                        <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
                            <Box sx={{ py: 2, px: 2 }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1.5, sm: 3 }}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ width: '100%' }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        Showing {totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1}–
                                        {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} countries
                                    </Typography>
                                    <Pagination
                                        count={Math.max(1, totalPages)}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size={isMobile ? 'small' : 'medium'}
                                        showFirstButton
                                        showLastButton
                                        disabled={loading || totalPages <= 1}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        Page {currentPage} of {Math.max(1, totalPages)}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* ── Add / Edit Drawer ── */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{ sx: { width: { xs: '100%', sm: 720 } } }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                        sx={{
                            px: 3, py: 2,
                            borderBottom: 1, borderColor: 'divider',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            flexShrink: 0,
                        }}
                    >
                        <Typography variant="h6">
                            {drawerMode === 'add' ? 'Add Shipping Country' : 'Edit Shipping Country'}
                        </Typography>
                        <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleSave(); }}
                        sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
                    >
                        {/* Static fields */}
                        <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Country Name"
                                        value={form.name}
                                        onChange={e => setForm({ ...form, name: e.target.value })}
                                        required
                                        placeholder="e.g., Sri Lanka"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Country Code"
                                        value={form.code}
                                        onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        required
                                        inputProps={{ maxLength: 3 }}
                                        helperText="e.g., USA, LKR, GBR"
                                        placeholder="LKR"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={`Default Shipping Cost (${currencySymbol})`}
                                        value={form.defaultShippingCost}
                                        onChange={e => setForm({ ...form, defaultShippingCost: Number(e.target.value) })}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        helperText="Applied when no state is selected"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={`Free Shipping Limit (${currencySymbol})`}
                                        value={form.freeShippingLimit}
                                        onChange={e => setForm({ ...form, freeShippingLimit: Number(e.target.value) })}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        helperText="Orders above this total get free shipping (0 = disabled)"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <FreeShippingIcon
                                                        fontSize="small"
                                                        sx={{ color: form.freeShippingLimit > 0 ? 'success.main' : 'text.disabled' }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1.5 }}>
                                <Typography variant="subtitle1" fontWeight={600}>States / Provinces</Typography>
                                <Button variant="outlined" size="small" onClick={addState} startIcon={<Add />}>
                                    Add State
                                </Button>
                            </Stack>
                        </Box>

                        {/* States list — scrollable */}
                        <Box
                            ref={statesContainerRef}
                            sx={{
                                flex: 1, overflow: 'auto', px: 3, pb: 3, minHeight: 0,
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
                                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
                            }}
                        >
                            {form.states.length === 0 ? (
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No states added yet. Click "Add State" to create specific shipping rates for different states or provinces.
                                    </Typography>
                                </Paper>
                            ) : (
                                <Stack spacing={1.5}>
                                    {form.states.map((state, index) => (
                                        <Paper key={state.id} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                                            <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                                                <Grid item xs>
                                                    <TextField
                                                        fullWidth
                                                        label="State Name"
                                                        value={state.name}
                                                        onChange={e => updateState(index, 'name', e.target.value)}
                                                        size="small"
                                                        autoFocus={index === form.states.length - 1}
                                                        placeholder="e.g., Western Province"
                                                    />
                                                </Grid>
                                                <Grid item xs="auto">
                                                    <Tooltip title="Remove State">
                                                        <IconButton color="error" onClick={() => removeState(index)} size="small">
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        label={`Cost (${currencySymbol})`}
                                                        value={state.shippingCost}
                                                        onChange={e => updateState(index, 'shippingCost', Number(e.target.value))}
                                                        inputProps={{ min: 0, step: 0.01 }}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        label={`Free Shipping Limit (${currencySymbol})`}
                                                        value={state.freeShippingLimit ?? 0}
                                                        onChange={e => updateState(index, 'freeShippingLimit', Number(e.target.value))}
                                                        inputProps={{ min: 0, step: 0.01 }}
                                                        size="small"
                                                        helperText="0 = disabled"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <FreeShippingIcon
                                                                        fontSize="small"
                                                                        sx={{ color: (state.freeShippingLimit ?? 0) > 0 ? 'success.main' : 'text.disabled' }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Box>

                        <Divider />
                        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
                            <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<Cancel />}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={saveLoading}
                                startIcon={saveLoading ? <CircularProgress size={16} /> : <Save />}
                            >
                                {drawerMode === 'add' ? 'Create Country' : 'Update Country'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* ── Delete Confirmation Dialog ── */}
            <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Shipping Country</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete{' '}
                        <strong>"{deleteTarget?.name}"</strong>?{' '}
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteDialogLoading}
                        startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <Delete />}
                    >
                        {deleteDialogLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Full-screen deleting overlay (mirrors EnhancedProductDrawer) ── */}
            <Backdrop
                open={deletingOverlay}
                sx={{
                    zIndex: (t) => t.zIndex.modal + 100,
                    flexDirection: 'column',
                    gap: 3,
                    bgcolor: 'rgba(0,0,0,0.6)',
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        px: 5,
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                        minWidth: 280,
                        boxShadow: 24,
                    }}
                >
                    <CircularProgress size={48} thickness={4} />
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
                            Deleting Country…
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                            Please wait, do not close this window
                        </Typography>
                    </Box>
                </Box>
            </Backdrop>

            {/* ── Full-screen saving overlay (mirrors EnhancedProductDrawer) ── */}
            <Backdrop
                open={savingOverlay}
                sx={{
                    zIndex: (t) => t.zIndex.modal + 100,
                    flexDirection: 'column',
                    gap: 3,
                    bgcolor: 'rgba(0,0,0,0.6)',
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        px: 5,
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                        minWidth: 280,
                        boxShadow: 24,
                    }}
                >
                    <CircularProgress size={48} thickness={4} />
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
                            {drawerMode === 'add' ? 'Creating Country…' : 'Updating Country…'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                            Please wait, do not close this window
                        </Typography>
                    </Box>
                </Box>
            </Backdrop>

            <ToastNotification
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={handleCloseToast}
            />
        </Box>
    );
};

export default ShippingManagement;